/**
 * Fulcrum map layers.
 *
 * A Fulcrum form is configured once, as an External Data Source. Its map layer
 * is derived from that entry at adapt time, the same way the Geotagged Photos
 * layer is synthesised and CM-table layers are generated by
 * `useCmTableMapLayers`, so no one has to author a layer (Fulcrum layers have
 * no URL to author one with).
 *
 * Styling still comes from the org: a stored `type: "fulcrum"` layer for the
 * same source (matched by name, or by form id if the source was renamed) is
 * laid over the defaults. Configs imported from a legacy `config.json` carry
 * exactly those entries, so their icons and GeoStyler styles keep working. A
 * layer with no icon or style gets the plain look an unstyled CM table has.
 */
import type { FulcrumLayerConfig } from "../types";
import { DEFAULT_FEATURE_COLOR, defaultFillPaint, defaultLinePaint, defaultPointPaint } from "./defaultLayerPaint";
import { styleForGeometry } from "./layerStyle";

/** The parts of an External Data Source a Fulcrum layer is derived from. */
export interface FulcrumLayerSource {
  name: string;
  formId?: string;
  /** How its records are drawn, set under Data sources: see {@link SourceGeometryType}. */
  geometryType?: string;
}

/**
 * How a data source's records are drawn. It belongs to the source (the Data
 * sources tab sets it) and decides its layer's geometry, which the Layers tab
 * shows but does not change. Each includes its multi-part form: points draw
 * multipoints, lines multilines, polygons multipolygons.
 */
export type SourceGeometryType = "point" | "line" | "polygon";

export const SOURCE_GEOMETRY_TYPES: Array<{ value: SourceGeometryType; label: string }> = [
  { value: "point", label: "Points" },
  { value: "line", label: "Lines" },
  { value: "polygon", label: "Polygons" }
];

const LAYER_GEOMETRY: Record<SourceGeometryType, string> = { point: "symbol", line: "line", polygon: "fill" };

const isSourceGeometryType = (value: unknown): value is SourceGeometryType => value === "point" || value === "line" || value === "polygon";

/**
 * A source's geometry type. A source saved before sources had one takes its
 * stored layer's way of drawing, so an existing org's map doesn't change.
 */
export const sourceGeometryTypeOf = (source: FulcrumLayerSource, stored?: { geometry?: string }): SourceGeometryType => {
  if (isSourceGeometryType(source.geometryType)) return source.geometryType;
  if (stored?.geometry === "line") return "line";
  if (stored?.geometry === "fill") return "polygon";
  return "point";
};

/** Whether a stored or configured layer is a Fulcrum layer. */
export const isFulcrumLayer = (layer: any): layer is FulcrumLayerConfig => layer?.type === "fulcrum";

/**
 * Pairs each source with its stored layer: the one named after it, or, after
 * the source was renamed, an unclaimed one for the same form whose name matches
 * no source.
 */
export const matchStoredFulcrumLayers = <T>(layers: T[], sources: FulcrumLayerSource[]): Map<FulcrumLayerSource, T> => {
  const stored = layers.filter(isFulcrumLayer) as unknown as Array<T & FulcrumLayerConfig>;
  const sourceNames = new Set(sources.map((source) => source.name));
  const matches = new Map<FulcrumLayerSource, T>();
  const claimed = new Set<T>();

  for (const source of sources) {
    const byName = stored.find((layer) => layer.id === source.name);
    if (byName) {
      matches.set(source, byName);
      claimed.add(byName);
    }
  }
  for (const source of sources) {
    if (matches.has(source) || !source.formId) continue;
    const byForm = stored.find((layer) => !claimed.has(layer) && !sourceNames.has(layer.id) && layer.formId === source.formId);
    if (byForm) {
      matches.set(source, byForm);
      claimed.add(byForm);
    }
  }
  return matches;
};

/** A Fulcrum layer as derived: its records are fetched by `formId`, so it has no URL. */
export type DerivedFulcrumLayer = Omit<FulcrumLayerConfig, "url">;

/**
 * The layer for one source: defaults, then the org's stored styling, with the
 * identity (id, type, form) and the geometry always taken from the source, so
 * the endpoint's `mapLayerId` (the source name) keeps pointing at it.
 */
export const buildFulcrumLayer = (source: FulcrumLayerSource, stored?: Partial<FulcrumLayerConfig>): DerivedFulcrumLayer => ({
  defaultIcon: "",
  initialVisibility: true,
  style: {},
  ...stored,
  id: source.name,
  type: "fulcrum",
  formId: source.formId ?? "",
  // The source decides how it is drawn, not the stored layer.
  geometry: LAYER_GEOMETRY[sourceGeometryTypeOf(source, stored)] as DerivedFulcrumLayer["geometry"]
});

const hasStyle = (style: object | undefined): boolean => !!style && Object.keys(style).length > 0;

/**
 * Gives a layer the org has not styled the plain look of an unstyled CM table:
 * points without an icon become circles, lines and areas without a style get
 * the default line or fill, all in `color` (the org's primary colour). A
 * GeoStyler style or an icon is the org's own styling and is left alone.
 */
export const withDefaultLook = (layer: DerivedFulcrumLayer, color: string = DEFAULT_FEATURE_COLOR): DerivedFulcrumLayer => {
  if (layer.geostyler) return layer;
  if (layer.geometry === "symbol" || layer.geometry === "circle") {
    if (layer.geometry === "symbol" && layer.defaultIcon) return layer;
    // Keep any circle paint the org did set; label/icon keys don't apply to circles.
    const circlePaint = Object.fromEntries(Object.entries(layer.style ?? {}).filter(([key]) => key.startsWith("circle-")));
    return { ...layer, geometry: "circle", style: { ...defaultPointPaint(color), ...circlePaint } };
  }
  // Keys left from another way of drawing it don't count as styling this one.
  if (hasStyle(styleForGeometry(layer.style, layer.geometry))) return layer;
  return { ...layer, style: layer.geometry === "line" ? defaultLinePaint(color) : defaultFillPaint(color) };
};

/**
 * Guarantees one Fulcrum layer per External Data Source. A stored layer for a
 * source is replaced in place by its derived layer (keeping the org's layer
 * order); sources with no stored layer are appended. Stored Fulcrum layers that
 * match no source are left untouched.
 */
export const withFulcrumLayers = <T extends { id?: string }>(
  layers: T[],
  sources: FulcrumLayerSource[] = [],
  color: string = DEFAULT_FEATURE_COLOR
): T[] => {
  const withForm = sources.filter((source) => !!source.formId);
  const matches = matchStoredFulcrumLayers(layers, withForm);
  const sourceOf = new Map(Array.from(matches, ([source, layer]) => [layer, source] as const));
  const derive = (source: FulcrumLayerSource, stored?: T) =>
    withDefaultLook(buildFulcrumLayer(source, stored as Partial<FulcrumLayerConfig> | undefined), color) as unknown as T;

  const merged = layers.map((layer) => {
    const source = sourceOf.get(layer);
    return source ? derive(source, layer) : layer;
  });
  const added = withForm.filter((source) => !matches.has(source)).map((source) => derive(source));
  return [...merged, ...added];
};

/**
 * Gives every source a geometry type. One saved before sources had it takes its
 * stored layer's, so the admin console shows, and next saves, what the map draws.
 */
export const withSourceGeometryTypes = <S extends FulcrumLayerSource>(sources: S[] = [], layers: unknown[] = []): S[] => {
  const matches = matchStoredFulcrumLayers(layers as Array<{ geometry?: string }>, sources);
  return sources.map((source) =>
    isSourceGeometryType(source.geometryType) ? source : { ...source, geometryType: sourceGeometryTypeOf(source, matches.get(source)) }
  );
};
